import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { usePredictions } from '../hooks/usePredictions'
import { supabase } from '../lib/supabase'

export default function AuthModal({ onClose }) {
  const { signIn, signUp } = useAuth()
  const { migrateAnonymousPredictions } = usePredictions()
  const [mode, setMode] = useState('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    setError(''); setSuccess(''); setLoading(true)
    try {
      if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + '/reset-password',
        })
        if (error) setError(error.message)
        else setSuccess('Check your email for a password reset link.')
        return
      }
      if (mode === 'signin') {
        const { error } = await signIn(email, password)
        if (error) setError(error.message)
        else onClose()
      } else {
        if (!username.trim()) { setError('Username is required'); return }
        if (username.length < 3) { setError('Username must be at least 3 characters'); return }
        if (!/^[a-zA-Z0-9_]+$/.test(username)) { setError('Letters, numbers, and underscores only'); return }

        // Check if username is already taken before creating the account
        const { data: existing } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', username.trim().toLowerCase())
          .maybeSingle()

        if (existing) {
          setError('That username is already taken. Please choose another.')
          return
        }

        const { data, error } = await signUp(email, password)
        if (error) { setError(error.message); return }
        if (data?.user) {
          // Save username to profiles now (id = auth user id, not user_id)
          await supabase.from('profiles').upsert({
            id: data.user.id,
            username: username.trim().toLowerCase(),
          }, { onConflict: 'id' })
          // Migrate any anonymous predictions
          await migrateAnonymousPredictions(data.user.id)
        }
        // Email confirmation is enabled — show confirmation message instead of closing
        // User won't have an active session until they click the email link
        setSuccess('Check your email to confirm your account, then sign in.')
      }
    } finally { setLoading(false) }
  }

  const titles = { signin: 'Welcome back', signup: 'Create account', reset: 'Reset password' }
  const subtitles = {
    signin: 'Sign in to track your predictions',
    signup: 'Free — save predictions and appear on the leaderboard',
    reset: 'Enter your email and we\'ll send a reset link',
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(250,250,248,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 300, backdropFilter: 'blur(4px)',
      padding: '16px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderTop: '3px solid var(--olive)', borderRadius: 6,
        padding: '28px 24px', width: '100%', maxWidth: 400,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        animation: 'fadeIn 0.2s ease',
      }}>
        <h2 style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
          {titles[mode]}
        </h2>
        <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 24 }}>
          {subtitles[mode]}
        </p>

        {error && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 4, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: 'var(--green-bg)', border: '1px solid rgba(74,124,63,0.2)', borderRadius: 4, padding: '10px 14px', fontSize: 13, color: 'var(--green)', marginBottom: 16 }}>
            {success}
          </div>
        )}

        <input type="email" placeholder="Email" value={email}
          onChange={e => setEmail(e.target.value)} style={inputStyle} />

        {mode !== 'reset' && (
          <input type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => mode === 'signin' && e.key === 'Enter' && handleSubmit()}
            style={{ ...inputStyle, marginTop: 10 }} />
        )}

        {mode === 'signup' && (
          <>
            <input type="text" placeholder="Username" value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{ ...inputStyle, marginTop: 10 }} />
            <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
              Shown publicly on the leaderboard. Letters, numbers, underscores only.
            </p>
          </>
        )}

        {!success && (
          <button onClick={handleSubmit} disabled={loading} style={{
            width: '100%', marginTop: 16, padding: '11px',
            background: 'var(--olive)', border: 'none', borderRadius: 4,
            color: '#fff', fontWeight: 600, fontSize: 14,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--sans)', opacity: loading ? 0.7 : 1,
          }}>
            {loading ? 'Loading...' : mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset link'}
          </button>
        )}

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--text3)' }}>
          {mode === 'signin' && (
            <>
              <button onClick={() => { setMode('reset'); setError(''); setSuccess('') }} style={linkBtn}>
                Forgot password?
              </button>
              <span style={{ margin: '0 8px' }}>·</span>
              <button onClick={() => { setMode('signup'); setError(''); setSuccess('') }} style={linkBtn}>
                Sign up
              </button>
            </>
          )}
          {mode === 'signup' && (
            <button onClick={() => { setMode('signin'); setError(''); setSuccess('') }} style={linkBtn}>
              Already have an account? Sign in
            </button>
          )}
          {mode === 'reset' && (
            <button onClick={() => { setMode('signin'); setError(''); setSuccess('') }} style={linkBtn}>
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '10px 14px',
  background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 4, color: 'var(--text)', fontSize: 14,
  fontFamily: 'var(--sans)', outline: 'none', display: 'block',
}

const linkBtn = {
  background: 'none', border: 'none', color: 'var(--olive)',
  cursor: 'pointer', fontSize: 13, fontFamily: 'var(--sans)', fontWeight: 600,
}
